import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Scanner } from "@yudiel/react-qr-scanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  Camera,
  Flashlight,
  SwitchCamera,
  Check,
  AlertCircle,
  X
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { v4 as uuidv4 } from "uuid";

const ScanPay = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1); // 1: scan, 2: confirm, 3: success
  const [scanResult, setScanResult] = useState(null);
  const [manualPayload, setManualPayload] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentResult, setPaymentResult] = useState(null);
  const [scannerEnabled, setScannerEnabled] = useState(true);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("environment");
  const [scanError, setScanError] = useState(null);

  // Fetch wallets
  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  // Parse QR mutation
  const parseMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await axios.post(`${API}/qr/parse?payload=${encodeURIComponent(payload)}`);
      return response.data;
    },
    onSuccess: (data) => {
      setScanResult(data);
      if (data.amount) {
        setPaymentAmount(data.amount.toString());
      }
      setScannerEnabled(false);
      setStep(2);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Invalid QR code");
      setScannerEnabled(true);
    }
  });

  // Pay mutation
  const payMutation = useMutation({
    mutationFn: async () => {
      const wallet = wallets?.find(w => w.currency === (scanResult?.currency || "JMD"));
      if (!wallet) throw new Error("Wallet not found");
      
      const response = await axios.post(`${API}/qr/pay`, {
        payload: manualPayload,
        amount: parseFloat(paymentAmount),
        wallet_id: wallet.wallet_id,
        idempotency_key: uuidv4()
      });
      return response.data;
    },
    onSuccess: (data) => {
      setPaymentResult(data);
      setStep(3);
      queryClient.invalidateQueries(["wallets"]);
      queryClient.invalidateQueries(["transactions"]);
      toast.success("Payment successful!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Payment failed");
    }
  });

  const handleScan = (result) => {
    if (result && result[0]?.rawValue && scannerEnabled) {
      const payload = result[0].rawValue;
      setManualPayload(payload);
      setScannerEnabled(false);
      parseMutation.mutate(payload);
    }
  };

  const handleManualSubmit = () => {
    if (!manualPayload) {
      toast.error("Please enter a QR payload");
      return;
    }
    parseMutation.mutate(manualPayload);
  };

  const handlePay = () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const wallet = wallets?.find(w => w.currency === (scanResult?.currency || "JMD"));
    if (!wallet || wallet.available_balance < parseFloat(paymentAmount)) {
      toast.error("Insufficient funds");
      return;
    }
    payMutation.mutate();
  };

  const formatCurrency = (amount, currency = "JMD") => {
    const symbols = { JMD: "J$", USD: "$", GBP: "£", CAD: "C$" };
    return `${symbols[currency] || ""}${parseFloat(amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  const currentWallet = wallets?.find(w => w.currency === (scanResult?.currency || "JMD"));

  const resetScanner = () => {
    setScannerEnabled(true);
    setScanResult(null);
    setManualPayload("");
    setPaymentAmount("");
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="scan-pay-page">
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => step > 1 && step < 3 ? setStep(step - 1) : navigate("/dashboard")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">
          {step === 3 ? "Payment Complete" : "Scan to Pay"}
        </h1>
      </header>

      <main className="px-5 max-w-lg mx-auto">
        {/* Step 1: Scan */}
        {step === 1 && (
          <div className="animate-slide-up">
            {/* Camera Scanner */}
            <div className="glass rounded-3xl p-4 mb-6 overflow-hidden">
              <div className="relative w-full aspect-square bg-black/50 rounded-2xl overflow-hidden">
                {scannerEnabled && (
                  <Scanner
                    onScan={handleScan}
                    onError={(error) => setScanError(error?.message || "Camera error")}
                    constraints={{
                      facingMode: cameraFacing,
                    }}
                    allowMultiple={false}
                    scanDelay={500}
                    components={{
                      audio: false,
                      torch: torchEnabled,
                    }}
                    styles={{
                      container: {
                        width: "100%",
                        height: "100%",
                      },
                      video: {
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      },
                    }}
                  />
                )}
                
                {/* Scanner overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-8 border-2 border-primary/50 rounded-xl" />
                  <div className="absolute top-8 left-8 w-10 h-10 border-t-3 border-l-3 border-primary rounded-tl-lg" />
                  <div className="absolute top-8 right-8 w-10 h-10 border-t-3 border-r-3 border-primary rounded-tr-lg" />
                  <div className="absolute bottom-8 left-8 w-10 h-10 border-b-3 border-l-3 border-primary rounded-bl-lg" />
                  <div className="absolute bottom-8 right-8 w-10 h-10 border-b-3 border-r-3 border-primary rounded-br-lg" />
                  
                  {/* Scanning line animation */}
                  <div className="absolute top-8 left-8 right-8 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent animate-scan" />
                </div>

                {/* Camera not available fallback */}
                {scanError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 text-white">
                    <Camera className="w-12 h-12 mb-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground text-center px-4">
                      Camera access denied or unavailable.
                      <br />Use manual entry below.
                    </p>
                  </div>
                )}
              </div>

              {/* Camera controls */}
              <div className="flex justify-center gap-4 mt-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setTorchEnabled(!torchEnabled)}
                  className={`rounded-full ${torchEnabled ? "bg-primary/20 text-primary" : ""}`}
                  data-testid="torch-btn"
                >
                  <Flashlight className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCameraFacing(prev => prev === "environment" ? "user" : "environment")}
                  className="rounded-full"
                  data-testid="switch-camera-btn"
                >
                  <SwitchCamera className="w-5 h-5" />
                </Button>
              </div>

              <p className="text-sm text-muted-foreground text-center mt-4">
                Position QR code within the frame
              </p>
            </div>

            {/* Manual entry */}
            <div className="glass rounded-2xl p-4 mb-6">
              <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-3">
                Or enter QR payload manually
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="Paste QR payload..."
                  className="input-glass flex-1"
                  value={manualPayload}
                  onChange={(e) => setManualPayload(e.target.value)}
                  data-testid="qr-payload-input"
                />
                <Button
                  className="btn-primary px-6"
                  onClick={handleManualSubmit}
                  disabled={parseMutation.isPending}
                  data-testid="parse-qr-btn"
                >
                  {parseMutation.isPending ? "..." : "Go"}
                </Button>
              </div>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Tip: Use a QR code from another user&apos;s Receive page
            </p>
          </div>
        )}

        {/* Step 2: Confirm */}
        {step === 2 && scanResult && (
          <div className="animate-slide-up">
            <div className="glass rounded-3xl p-6 mb-6 text-center">
              {/* Payee info */}
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                {scanResult.payee_picture ? (
                  <img src={scanResult.payee_picture} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-primary">
                    {scanResult.payee_name?.charAt(0) || "?"}
                  </span>
                )}
              </div>
              <h2 className="font-heading font-semibold text-xl mb-1">
                {scanResult.payee_name}
              </h2>
              {scanResult.note && (
                <p className="text-sm text-muted-foreground mb-4">&quot;{scanResult.note}&quot;</p>
              )}

              {/* Amount input */}
              <div className="mt-6">
                <label className="text-xs uppercase tracking-widest text-muted-foreground/70">
                  Amount to pay
                </label>
                <div className="relative mt-2">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-muted-foreground">
                    {scanResult.currency === "USD" ? "$" : "J$"}
                  </span>
                  <Input
                    type="number"
                    className="input-glass pl-12 text-3xl font-mono h-16 text-center"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    readOnly={!!scanResult.amount}
                    data-testid="payment-amount-input"
                  />
                </div>
                {scanResult.amount && (
                  <p className="text-xs text-muted-foreground mt-2">Amount set by recipient</p>
                )}
              </div>
            </div>

            {/* Wallet info */}
            <div className="glass rounded-2xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">From {scanResult.currency} Wallet</span>
              </div>
              <span className="font-mono text-sm">
                {formatCurrency(currentWallet?.available_balance || 0, scanResult.currency)}
              </span>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 btn-secondary h-14"
                onClick={resetScanner}
                data-testid="cancel-btn"
              >
                <X className="w-5 h-5 mr-2" />
                Cancel
              </Button>
              <Button
                className="flex-1 btn-primary h-14 text-base"
                onClick={handlePay}
                disabled={payMutation.isPending}
                data-testid="confirm-pay-btn"
              >
                {payMutation.isPending ? "Processing..." : `Pay ${formatCurrency(paymentAmount || 0, scanResult.currency)}`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && paymentResult && (
          <div className="animate-slide-up text-center">
            <div className="glass rounded-3xl p-8 mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                <Check className="w-10 h-10 text-primary" />
              </div>
              
              <h2 className="font-heading font-bold text-2xl mb-2">Payment Sent!</h2>
              <p className="text-muted-foreground mb-6">
                Your payment has been completed
              </p>

              <div className="space-y-3 text-left">
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">To</span>
                  <span className="font-medium">{paymentResult.payee_name}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-mono font-medium text-primary">
                    {formatCurrency(paymentResult.amount, paymentResult.currency)}
                  </span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-muted-foreground">Reference</span>
                  <span className="font-mono text-sm">{paymentResult.payment_id}</span>
                </div>
              </div>
            </div>

            <Button
              className="w-full btn-primary h-14 text-base"
              onClick={() => navigate("/dashboard")}
              data-testid="done-btn"
            >
              Done
            </Button>
          </div>
        )}
      </main>

      {/* Add scanning animation CSS */}
      <style>{`
        @keyframes scan {
          0% { top: 2rem; opacity: 0; }
          50% { opacity: 1; }
          100% { top: calc(100% - 2rem); opacity: 0; }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ScanPay;
